import React, { ReactNode } from 'react';
import { ChatInputProvider } from '@/providers/chat-input-provider';
import { OnboardingProgressProvider } from '@/providers/onboarding-progress-provider';
import { CleanupProgressProvider } from '@/providers/cleanup-progress-provider';
import { MigrationProgressProvider } from '@/providers/migration-progress-provider';
import { MigrationProgressModal } from '@/components/migration-progress/migration-progress-modal';
import { MigrationProgressPill } from '@/components/migration-progress/migration-progress-pill';
import { AsyncTasksProvider } from '@/providers/async-tasks-provider';
import { ActionsProvider } from '@/providers/actions-provider';
import { BackupProvider } from '@/providers/backup-provider';
import { ProgressSidebar } from '@/components/onboarding-progress/progress-sidebar';
import { CleanupProgressSidebar } from '@/components/cleanup-progress/cleanup-progress-sidebar';
import { MigrationProgressSidebar } from '@/components/migration-progress/migration-progress-sidebar';
import { MigrationResourcesSidebar } from '@/components/migration-progress/migration-resources-sidebar';
import { ResourcesSidebar } from '@/components/resources/resources-sidebar';
import { ControlPanelSidebar } from '@/components/control-panel/control-panel-sidebar';
import { paths } from '@/config/paths';

export interface NavItem {
  label: string;
  to: string;
}

export interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export interface AgentLayout {
  navItems: NavItem[];
  LeftSidebar: React.ComponentType<SidebarProps> | null;
  RightSidebar: React.ComponentType<SidebarProps> | null;
  wrapWithProviders: (threadId: string | null, children: ReactNode) => ReactNode;
}

export const LAYOUT_REGISTRY: Record<string, AgentLayout> = {
  'onboarding-agent': {
    navItems: [
      { label: 'Best Practices', to: paths.BestPractices },
    ],
    LeftSidebar: ProgressSidebar,
    RightSidebar: ResourcesSidebar,
    wrapWithProviders: (threadId, children) => (
      <OnboardingProgressProvider threadId={threadId}>
        <ChatInputProvider>{children}</ChatInputProvider>
      </OnboardingProgressProvider>
    ),
  },
  generic: {
    navItems: [],
    LeftSidebar: null,
    RightSidebar: null,
    wrapWithProviders: (_threadId, children) => (
      <ChatInputProvider>{children}</ChatInputProvider>
    ),
  },
  'optimize-clean-up-agent': {
    navItems: [
      { label: 'Best Practices', to: paths.BestPracticesOptimizationChecks },
      { label: 'Rule Summary', to: paths.RuleSummary },
    ],
    LeftSidebar: CleanupProgressSidebar,
    RightSidebar: ControlPanelSidebar,
    wrapWithProviders: (threadId, children) => (
      <ActionsProvider>
        <BackupProvider>
          <AsyncTasksProvider threadId={threadId}>
            <CleanupProgressProvider threadId={threadId}>
              <ChatInputProvider>{children}</ChatInputProvider>
            </CleanupProgressProvider>
          </AsyncTasksProvider>
        </BackupProvider>
      </ActionsProvider>
    ),
  },
  // NOTE: id keeps the historical typo "assitant" to match the backend
  // agent_id (see AGENT_REGISTRY in agent-config.ts).
  'pd_migration_assitant': {
    navItems: [],
    // Journey-level progress sidebar + right "Quick Guides" sidebar.
    // The block coming from main still pointed at the now-removed
    // ``migration_status_tracker_ui_tool`` inline card (registered as a
    // push_ui_message component). That tool has been retired in the
    // backend (see ``ui_components_registry``) and replaced with the
    // sidebars + modal below, so this resolution keeps HEAD's wiring.
    LeftSidebar: MigrationProgressSidebar,
    RightSidebar: MigrationResourcesSidebar,
    wrapWithProviders: (threadId, children) => (
      <MigrationProgressProvider threadId={threadId}>
        <ChatInputProvider>{children}</ChatInputProvider>
        {/* Auto-opens while ``full_migration_execution`` runs; the pill
            below stays visible after close as the re-open affordance. */}
        <MigrationProgressModal />
        <MigrationProgressPill />
      </MigrationProgressProvider>
    ),
  },
};
